import AddIcon from "@mui/icons-material/Add";
import { type Dispatch, type FC, type SetStateAction, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { facilityHint, facilityOptionOf, formAfterFacilityPick, NEW_FACILITY_OPTION } from "./facilityChoice";
import { useHospitalFacilities } from "./facilityHooks";
import type { GeneralFormData } from "./types";

/** Hints span the whole section row, like the timezone banner beside them. */
const FULL_ROW_SX = { gridColumn: "1 / -1" };

/**
 * The facility (building) a new department is created in: one its network already has, or a new one.
 *
 * Shown only while creating a department; which facility an existing department is in isn't edited here.
 * Picking a listed facility also sets the form's time zone to the facility's.
 * Renders as section-grid cells (picker, new-facility name, hint), so it sits inside a `SettingsSection`.
 */
export const FacilityField: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
}> = ({ formData, setFormData }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { track } = useAnalytics();
	const fieldId = useId();
	const { data, isFetching, isError, retryFetch, network, isSettling } = useHospitalFacilities({ hospital: formData.hospital, enabled: userInfo.support });
	// While the typed network is still settling, the list on hand is the previous network's, so none is offered.
	const listed = isSettling ? undefined : data;
	const choice = formData.facility;
	const hint = facilityHint(network, listed, choice);

	const items = useMemo(
		() => [
			// DEFAULT: no list (network blank or settling, loading, or failed) offers only "New facility"; the hint below says which.
			...(listed ?? []).map((facility) => ({ value: facility.facility_id, label: facility.name, secondaryLabel: facility.timezone })),
			{ value: NEW_FACILITY_OPTION, label: t("department.config.general.facility.newOption"), icon: <AddIcon fontSize="small" /> },
		],
		[listed, t],
	);

	const newNameValidation = useMemo(
		() => ({
			validationCallback: (name: string) => name.trim() !== "",
			helperText: { invalid: t("department.config.general.facility.newName.required") },
			performValidation: "always" as const,
			showValidWhenValid: "never" as const,
		}),
		[t],
	);

	const handlePick = (option?: string) => {
		if (!option) return;
		track("dept_config_facility_selected", { kind: option === NEW_FACILITY_OPTION ? "new" : "existing", network });
		setFormData((previous) => formAfterFacilityPick(previous, option, listed));
	};

	return (
		<>
			<SystemDropdown
				fullWidth
				id={fieldId}
				label={t("department.config.general.facility.label")}
				required
				errorKey={choice === null ? "department.config.general.facility.required" : undefined}
				items={items}
				selectedItem={facilityOptionOf(choice)}
				onChange={handlePick}
				loading={isFetching}
				data-testid="department-config-facility-select"
			/>
			{choice?.kind === "new" && (
				<SystemValidationTextField
					label={t("department.config.general.facility.newName.label")}
					value={choice.name}
					onChange={(event) => setFormData((previous) => ({ ...previous, facility: { kind: "new", name: event.target.value } }))}
					required
					fullWidth
					validation={newNameValidation}
					dataTestId="department-config-facility-new-name-input"
				/>
			)}
			{isError ? (
				<InlineHint
					tone="warning"
					dataTestId="department-config-facility-load-failed"
					actionPlacement="below"
					action={
						<SystemButton buttonVariant="borderless" size="small" onClick={() => void retryFetch()} data-testid="department-config-facility-retry-button">
							{t("retry")}
						</SystemButton>
					}
					sx={FULL_ROW_SX}
				>
					{t("department.config.general.facility.loadFailed")}
				</InlineHint>
			) : (
				<InlineHint tone="neutral" dataTestId="department-config-facility-hint" sx={FULL_ROW_SX}>
					{hint.key === "department.config.general.facility.hint.existing" ? t(hint.key, hint.values) : t(hint.key)}
				</InlineHint>
			)}
		</>
	);
};
